import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { toast } from "react-toastify";
import { ApiURL } from "../../../../helpers/APIdata/apiURL";

import AppModal from "../../../../Components/Common/AppModal";
import RichTextEditor from "../../../../Components/RichTextEditor";
import ViewAndAddNewNotes from "../../../../Components/Common/ViewAndAddNewNotes";
import { handleRichTextOutput } from "../../../../Components/Common/HandleRichTextOutput";
import { uploadRecordingToDigitalOcean } from "../../../../Components/ScreenRecorder/uploadToDigitalOcean";
import {
  systemChangeDataApiCall,
  systemChangeUpdateApiCall,
  systemChangeDeleteApiCall,
  systemChangeAssignBundleApiCall,
  systemChangeNoteCreateApiCall,
  systemChangeAttachmentPreauthApiCall,
  systemChangeAttachmentDeleteApiCall,
} from "../../../../helpers/fakebackend_helper";
import { bundleLabel, formatDate, isReleased, noteAuthor } from "./shared";
import AffectedSection from "./AffectedSection";

// Rich-text values are stored as {"json": <lexical state>, "html": "..."}.
// Change #100003 - how each logged field is named in the history.
const HISTORY_FIELD_LABELS = {
  Title: "Title",
  Description: "Description",
  SystemChangeStatus_Id: "Status",
  SystemChangeType_Id: "Type",
  SystemChangePriority_Id: "Priority",
  SystemChangeBundle_Id: "Bundle",
  AssignedUser_Id: "Assigned to",
  DueDateTime: "Due",
  CompletedDateTime: "Completed",
};

const SOURCE_LABELS = {
  MANUAL: "by hand",
  REPORT_A_BUG: "from Report a Bug",
  SERVER_ERROR: "from Server Errors",
  COMPANY3_TASK: "from company 3",
  AI: "by AI",
};

// Change #100698 - "sometimes we need to download the change request as its
// for a manager not a system bug". GET /systemchange/pdf/{id} draws the
// details, description, screenshots and notes; this saves it under the name
// the server gives (or the same name built here if the header is hidden).
const downloadChangePdf = async (changeId, title) => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
  const response = await fetch(`${ApiURL().API_URL}/systemchange/pdf/${changeId}`, {
    headers: authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`The PDF could not be created (${response.status})`);
  }
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="([^"]+)"/);
  const fallback = `Change ${changeId}${title ? ` - ${title}` : ""}`
    .replace(/[^A-Za-z0-9 _\-()]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = match ? match[1] : `${fallback}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// One sentence per history row.
const describeHistory = (row) => {
  if (row.Action === "CREATED") {
    return `Created${SOURCE_LABELS[row.NewLabel] ? ` ${SOURCE_LABELS[row.NewLabel]}` : ""}`;
  }
  if (row.Action === "DELETED") return "Deleted";
  const field = HISTORY_FIELD_LABELS[row.FieldName] || row.FieldName;
  if (row.FieldName === "Description") return "Description edited";
  const from = row.OldLabel || "none";
  const to = row.NewLabel || "none";
  return `${field}: ${from} → ${to}`;
};

const isLexicalOrEmpty = (value) => {
  if (!value) return true;
  try {
    return !!JSON.parse(value)?.json;
  } catch (e) {
    return false;
  }
};

/**
 * System Changes (CRMit Task 24912) - one change: its fields (saved as they are
 * changed), description, notes and attachments. Opened from any tab's grid.
 */
const ChangeDrawer = ({ changeId, lookups, onClose, onChanged }) => {
  const [change, setChange] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [history, setHistory] = useState([]);
  // Change #100028 - who to tell when it is fixed.
  const [affected, setAffected] = useState([]);
  const [affectedReady, setAffectedReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [editingDescription, setEditingDescription] = useState(false);
  const [description, setDescription] = useState("");

  const [noteText, setNoteText] = useState("");
  const [isClearEditor, setIsClearEditor] = useState(false);

  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = async () => {
    if (!changeId) return;
    setLoading(true);
    try {
      const response = await systemChangeDataApiCall(changeId);
      if (response?.status === 200) {
        setChange(response.systemchange_data);
        setTitle(response.systemchange_data?.Title || "");
        setNotes(response.systemchangenote_list || []);
        setAttachments(response.systemchangeattachment_list || []);
        setHistory(response.systemchangelog_list || []);
        setAffected(response.systemchangeaffected_list || []);
        setAffectedReady(!!response.systemchangeaffected_ready);
      } else {
        toast.error(response?.message || "Could not load the change");
      }
    } catch (e) {
      toast.error("Could not load the change");
    }
    setLoading(false);
  };

  useEffect(() => {
    setChange(null);
    setEditingDescription(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeId]);

  // One field at a time, saved as soon as it is changed.
  const saveFields = async (fields) => {
    setSaving(true);
    try {
      const response = await systemChangeUpdateApiCall([{ Id: changeId, ...fields }]);
      if (response?.status === 200) {
        await load();
        onChanged?.();
      } else {
        toast.error(response?.message || "Could not save");
      }
    } catch (e) {
      toast.error("Could not save");
    }
    setSaving(false);
  };

  const saveBundle = async (bundleId) => {
    setSaving(true);
    try {
      const response = await systemChangeAssignBundleApiCall([
        { SystemChangeBundle_Id: bundleId || null, SystemChange_Ids: [changeId] },
      ]);
      if (response?.status === 200) {
        toast.success(response.message);
        await load();
        onChanged?.();
      } else {
        toast.error(response?.message || "Could not change the bundle");
      }
    } catch (e) {
      toast.error("Could not change the bundle");
    }
    setSaving(false);
  };

  const handleAddNote = async () => {
    try {
      const response = await systemChangeNoteCreateApiCall([
        { SystemChange_Id: changeId, Description: noteText },
      ]);
      if (response?.status === 200) {
        setNoteText("");
        setIsClearEditor(true);
        await load();
        onChanged?.();
      } else {
        toast.error(response?.message || "Could not add the note");
      }
    } catch (e) {
      toast.error("Could not add the note");
    }
  };

  // Two steps, as the task attachments do: the API records the file and hands
  // back a presigned URL, then the browser PUTs the bytes straight to Spaces.
  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      let attachmentId = null;
      try {
        const response = await systemChangeAttachmentPreauthApiCall([
          {
            SystemChange_Id: changeId,
            FileName: file.name,
            FileType: file.type || null,
            FileSizeBytes: file.size,
          },
        ]);
        const preauth = response?.preauth_data?.[0];
        attachmentId = preauth?.SystemChangeAttachment_Id;
        if (!preauth?.PresignedUrl) {
          throw new Error(response?.message || "No upload URL returned");
        }
        await uploadRecordingToDigitalOcean({
          uploadUrl: preauth.PresignedUrl,
          blob: file,
          contentType: file.type || "application/octet-stream",
        });
      } catch (e) {
        toast.error(`${file.name}: ${e?.message || "upload failed"}`);
        // Do not leave a row pointing at a file that never arrived.
        if (attachmentId) {
          systemChangeAttachmentDeleteApiCall({ Id: attachmentId }).catch(() => {});
        }
      }
    }
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
    load();
  };

  const handleDeleteAttachment = async (attachment) => {
    try {
      const response = await systemChangeAttachmentDeleteApiCall({ Id: attachment.Id });
      if (response?.status === 200) load();
      else toast.error(response?.message || "Could not remove the file");
    } catch (e) {
      toast.error("Could not remove the file");
    }
  };

  const handleDeleteChange = async () => {
    try {
      const response = await systemChangeDeleteApiCall({ Id: changeId });
      if (response?.status === 200) {
        toast.success(`Change #${changeId} deleted`);
        setConfirmDelete(false);
        onChanged?.();
        onClose();
      } else {
        toast.error(response?.message || "Could not delete the change");
      }
    } catch (e) {
      toast.error("Could not delete the change");
    }
  };

  const lookupSelect = (label, field, options, emptyLabel = "None") => (
    <TextField
      select
      size="small"
      label={label}
      value={change?.[field] ?? ""}
      disabled={saving}
      onChange={(e) => saveFields({ [field]: e.target.value || null })}
      fullWidth
    >
      <MenuItem value="">
        <em>{emptyLabel}</em>
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option.Id} value={option.Id}>
          {option.Name}
          {Number(option.FlagComplete) === 1 ? "  (done)" : ""}
        </MenuItem>
      ))}
    </TextField>
  );

  // Inactive lookups stay selectable for the change that already uses one.
  const activeOrCurrent = (list, current) =>
    (list || []).filter(
      (row) => Number(row.FlagActive) === 1 || String(row.Id) === String(current)
    );

  const currentBundle = (lookups?.bundles || []).find(
    (bundle) => String(bundle.Id) === String(change?.SystemChangeBundle_Id)
  );
  const bundleLocked = isReleased(currentBundle);

  // ViewAndAddNewNotes resolves authors through a staff list; give it one
  // built from the notes themselves.
  const noteRows = notes.map((note) => ({ ...note, CreatedStaffId: note.Id }));
  const noteAuthors = notes.map((note) => ({ Id: note.Id, FullName: noteAuthor(note) }));

  return (
    <Drawer
      anchor="right"
      open={!!changeId}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 900 } } }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 2 }}>
          <Typography variant="h6">Change #{changeId}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Download as PDF (details, description, screenshots and notes)">
              <span>
                <IconButton
                  size="small"
                  disabled={!change || pdfBusy}
                  onClick={async () => {
                    setPdfBusy(true);
                    try {
                      await downloadChangePdf(changeId, change?.Title);
                    } catch (error) {
                      toast.error(error?.message || "The PDF could not be created.");
                    }
                    setPdfBusy(false);
                  }}
                >
                  {pdfBusy ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete this change">
              <IconButton size="small" onClick={() => setConfirmDelete(true)}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {loading && !change && <CircularProgress size={22} />}

        {change && (
          <>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() =>
                title.trim() && title !== change.Title && saveFields({ Title: title })
              }
              fullWidth
              multiline
              sx={{ mb: 2 }}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 2,
                mb: 2,
              }}
            >
              {lookupSelect(
                "Status",
                "SystemChangeStatus_Id",
                activeOrCurrent(lookups?.statuses, change.SystemChangeStatus_Id)
              )}
              {lookupSelect(
                "Type",
                "SystemChangeType_Id",
                activeOrCurrent(lookups?.types, change.SystemChangeType_Id)
              )}
              {lookupSelect(
                "Priority",
                "SystemChangePriority_Id",
                activeOrCurrent(lookups?.priorities, change.SystemChangePriority_Id)
              )}
              <TextField
                select
                size="small"
                label="Assigned to"
                value={change.AssignedUser_Id ?? ""}
                disabled={saving}
                onChange={(e) => saveFields({ AssignedUser_Id: e.target.value || null })}
                helperText={
                  !change.AssignedUser_Id && change.AssignedName
                    ? `From company 3: ${change.AssignedName}`
                    : " "
                }
                fullWidth
              >
                <MenuItem value="">
                  <em>Nobody</em>
                </MenuItem>
                {(lookups?.assignees || []).map((user) => (
                  <MenuItem key={user.Id} value={user.Id}>
                    {user.UserFullName || user.Email}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Bundle"
                value={change.SystemChangeBundle_Id ?? ""}
                disabled={saving || bundleLocked}
                onChange={(e) => saveBundle(e.target.value)}
                helperText={bundleLocked ? "Released - re-open the bundle to change" : " "}
                fullWidth
              >
                <MenuItem value="">
                  <em>Not bundled</em>
                </MenuItem>
                {(lookups?.bundles || [])
                  .filter(
                    (bundle) =>
                      !isReleased(bundle) ||
                      String(bundle.Id) === String(change.SystemChangeBundle_Id)
                  )
                  .map((bundle) => (
                    <MenuItem key={bundle.Id} value={bundle.Id}>
                      {bundleLabel(bundle)}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField
                type="date"
                size="small"
                label="Due"
                value={change.DueDateTime ? change.DueDateTime.slice(0, 10) : ""}
                disabled={saving}
                onChange={(e) =>
                  saveFields({ DueDateTime: e.target.value ? `${e.target.value} 00:00:00` : null })
                }
                InputLabelProps={{ shrink: true }}
                helperText=" "
                fullWidth
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Raised {formatDate(change.CreatedDateTime, "DD/MM/YYYY HH:mm")}
              {change.ReportedUser_FullName ? ` by ${change.ReportedUser_FullName}` : ""}
              {change.CompletedDateTime
                ? ` - completed ${formatDate(change.CompletedDateTime, "DD/MM/YYYY HH:mm")}`
                : ""}
              {change.SourceType === "COMPANY3_TASK"
                ? ` - copied from company 3 task #${change.SourceTask_Id} on ${formatDate(change.MigratedDateTime)}`
                : ""}
              {change.ServerErrorLog_Id ? ` - from server error #${change.ServerErrorLog_Id}` : ""}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Description
              </Typography>
              {!editingDescription ? (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setDescription(change.Description || "");
                    setEditingDescription(true);
                  }}
                >
                  Edit
                </Button>
              ) : (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" color="inherit" onClick={() => setEditingDescription(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={saving}
                    onClick={async () => {
                      await saveFields({ Description: description });
                      setEditingDescription(false);
                    }}
                  >
                    Save
                  </Button>
                </Box>
              )}
            </Box>
            <Box sx={{ mb: 3, wordBreak: "break-word" }}>
              {editingDescription && isLexicalOrEmpty(change.Description) && (
                <RichTextEditor
                  initialValue={change.Description || ""}
                  receivedContent={change.Description || ""}
                  placeholder="Describe the change..."
                  handleContentChange={setDescription}
                />
              )}
              {/* Plain-text descriptions (old Trello imports, API-raised
                  changes) cannot be loaded into the rich-text editor - it would
                  open blank and saving would wipe them - so they are edited as
                  the plain text they are. */}
              {editingDescription && !isLexicalOrEmpty(change.Description) && (
                <TextField
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={8}
                />
              )}
              {!editingDescription && (
                handleRichTextOutput(change.Description) || (
                  <Typography color="text.secondary">No description</Typography>
                )
              )}
            </Box>

            {/* Change #100028 - companies / users to tell when it is fixed. */}
            <AffectedSection
              change={change}
              rows={affected}
              ready={affectedReady}
              onChanged={() => {
                load();
                onChanged?.();
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Attachments ({attachments.length})
              </Typography>
              <Button
                size="small"
                startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon />}
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
              >
                {uploading ? "Uploading..." : "Attach files"}
              </Button>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => handleUpload(e.target.files)}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              {attachments.length === 0 && (
                <Typography color="text.secondary">No files</Typography>
              )}
              {attachments.map((attachment) => (
                <Box
                  key={attachment.Id}
                  sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
                >
                  {attachment.FileUrl ? (
                    <a href={attachment.FileUrl} target="_blank" rel="noopener noreferrer">
                      {attachment.Name || attachment.FileName}
                    </a>
                  ) : (
                    <span>{attachment.Name || attachment.FileName}</span>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(attachment.CreatedDateTime)}
                  </Typography>
                  <IconButton size="small" onClick={() => handleDeleteAttachment(attachment)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Notes ({notes.length})
            </Typography>
            <ViewAndAddNewNotes
              dataList={noteRows}
              staffData={noteAuthors}
              description={noteText}
              setDescription={setNoteText}
              handleAddNewNotes={handleAddNote}
              isClearEditor={isClearEditor}
              setIsClearEditor={setIsClearEditor}
            />

            {/* Change #100003 - who changed what, newest first. */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
              History ({history.length})
            </Typography>
            {history.length === 0 && (
              <Typography color="text.secondary">
                No changes recorded yet. Changes made from now on are listed here.
              </Typography>
            )}
            {history.map((row) => (
              <Box
                key={row.Id}
                sx={{
                  display: "flex",
                  gap: 2,
                  py: 0.75,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 110, pt: 0.25 }}
                >
                  {formatDate(row.CreatedDateTime, "DD/MM/YYYY HH:mm")}
                </Typography>
                <Box sx={{ minWidth: 0, wordBreak: "break-word" }}>
                  <Typography variant="body2">{describeHistory(row)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.CreatedUser_FullName || "CRMit API"}
                  </Typography>
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>

      <AppModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete change #${changeId}?`}
        onSubmit={handleDeleteChange}
        submitLabel="Delete"
        submitColor="error"
      >
        <Typography>
          It disappears from every tab and bundle. The company 3 task it came from, if
          any, is not touched.
        </Typography>
      </AppModal>
    </Drawer>
  );
};

export default ChangeDrawer;
